class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.emptyStateEl = this.querySelector('[data-search-empty-state]');

    this.setupEventListeners();
    if (this.emptyStateEl) this.setupEmptyState();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  // ── Empty State ──────────────────────────────────────────────────────────

  setupEmptyState() {
    this.emptyStateEl.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove-recent]');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        this.removeRecentSearch(removeBtn.dataset.removeRecent);
        this.renderRecentSearches();
        return;
      }

      const pill = e.target.closest('[data-search-term]');
      if (pill) {
        this.input.value = pill.dataset.searchTerm;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.focus();
      }
    });

    this.renderRecentSearches();
  }

  openEmptyState() {
    if (!this.emptyStateEl) return;
    this.renderRecentSearches();
    this.emptyStateEl.classList.add('is-visible');
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  hideEmptyState() {
    if (!this.emptyStateEl) return;
    this.emptyStateEl.classList.remove('is-visible');
  }

  // ── Recent Searches (localStorage) ──────────────────────────────────────

  static get storageKey() {
    return 'kymee_recent_searches';
  }

  getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem(PredictiveSearch.storageKey) || '[]');
    } catch {
      return [];
    }
  }

  saveRecentSearch(term) {
    if (!term || term.trim().length < 2) return;
    const trimmed = term.trim();
    const searches = this.getRecentSearches().filter(
      (s) => s.toLowerCase() !== trimmed.toLowerCase()
    );
    searches.unshift(trimmed);
    localStorage.setItem(PredictiveSearch.storageKey, JSON.stringify(searches.slice(0, 5)));
  }

  removeRecentSearch(term) {
    const searches = this.getRecentSearches().filter((s) => s !== term);
    localStorage.setItem(PredictiveSearch.storageKey, JSON.stringify(searches));
  }

  renderRecentSearches() {
    if (!this.emptyStateEl) return;

    const section = this.emptyStateEl.querySelector('[data-recent-searches-section]');
    const list = this.emptyStateEl.querySelector('[data-recent-searches-list]');
    if (!section || !list) return;

    const searches = this.getRecentSearches();
    if (searches.length === 0) {
      section.style.display = 'none';
      return;
    }

    section.style.display = 'block';
    list.innerHTML = searches
      .map((term) => {
        const escaped = term
          .replace(/&/g, '&amp;')
          .replace(/"/g, '&quot;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        return `<button type="button" class="search-pill" data-search-term="${escaped}">
          ${escaped}
          <span class="search-pill-remove" data-remove-recent="${escaped}" aria-label="Remove ${escaped}">&times;</span>
        </button>`;
      })
      .join('');
  }

  // ── Core Methods ─────────────────────────────────────────────────────────

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      this.openEmptyState();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (query.length) this.saveRecentSearch(query);
    if (!query.length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      setTimeout(() => this.openEmptyState());
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.openEmptyState();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement) && this.getQuery().length > 0) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
      this.openEmptyState();
      return;
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=product,collection,query&resources[limit]=8&resources[limit_scope]=each`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(resultsMarkup, 'text/html');
  doc.querySelectorAll('li, a, span, p').forEach(el => {
    if (/for\s+mens?$/i.test(el.textContent.trim())) {
      const li = el.closest('li');
      if (li) li.remove();
    }
  });
  this.predictiveSearchResults.innerHTML = doc.body.innerHTML;

    // Newly-injected .wishlist_button elements (inside card-product) start
    // with no "active" state — wishlist.js only syncs that on page load and
    // on its own click handler. Re-use its existing "wishlist:updated"
    // listener to re-sync against localStorage now that these buttons exist.
    document.dispatchEvent(new CustomEvent('wishlist:updated'));

    this.hideEmptyState();
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]').textContent
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    this.hideEmptyState();

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
